import * as React from 'react'
import { connect } from 'react-redux'
import type { EmailCampaign } from 'types/campaign'
import type { Contact } from 'types/list'
import type { State } from 'types/redux'
import { campaignDetailsUpdate } from '@disco/state-management/actions/campaign-details-actions'
import {
  getCampaign,
  MAXIMUM_RECIPIENTS,
} from '@disco/state-management/selectors/campaigns'
import { getContactsFilteredByCampaignRules } from '@disco/state-management/selectors/contacts-all'
import Button from 'components/ui/button'
import Icon from 'components/ui/icon'
import LoadingIndicator from 'components/ui/loading-indicator'
import ModalDialog from 'components/ui/modal-dialog'
import PageHeader from 'components/ui/page-header'
import { Brief } from 'types/brief'
import ButtonContainer from 'components/ui/button-container'
import styles from './index.module.css'

function mapStateToProps(
  state: State,
  ownProps: {
    campaignId?: string
  },
): any {
  return {
    campaign: ownProps.campaignId
      ? getCampaign(state, {
          campaignId: parseInt(ownProps.campaignId),
        })
      : null,
    filteredContacts:
      getContactsFilteredByCampaignRules(state).filteredContacts,
  }
}

const mapDispatchToProps = {
  campaignDetailsUpdate,
}
const BRIEFING_ENABLED = false
const ONE_MINUTE = 60 * 1000
type Props = {
  campaign: EmailCampaign | null | undefined
  campaignDetailsUpdate: typeof campaignDetailsUpdate
  nextDisabled?: boolean
  onNextClick?: () => unknown
  nextButton?: React.ReactNode
  filteredContacts: Contact[]
  customPageTitle?: string
}

export const CampaignSaveStatus = ({
  campaign,
}: {
  campaign: EmailCampaign | Brief
}) => {
  if (!campaign || campaign.loading) return null

  const { saving, dirty, savedAt } = campaign

  if (!saving && savedAt) {
    const savedAgo = Date.now() - new Date(savedAt).getTime()
    const isSavedRecently = savedAgo < ONE_MINUTE
    if (isSavedRecently) {
      return (
        <div
          className={styles.campaignHeaderSaving}
          data-hidden={dirty || null}
        >
          <Icon type="check" />
          <span className={styles.campaignHeaderSavingLabel}>Saved</span>
        </div>
      )
    } else {
      return (
        <div
          className={styles.campaignHeaderSaving}
          data-hidden={dirty || null}
        >
          <Icon type="check" />
          <span className={styles.campaignHeaderSavingLabel}>
            Saved {Math.floor(savedAgo / ONE_MINUTE)} min ago
          </span>
        </div>
      )
    }
  }

  return (
    <div className={styles.campaignHeaderSaving} data-hidden={!saving || null}>
      <LoadingIndicator />
      <span className={styles.campaignHeaderSavingLabel}>Saving</span>
    </div>
  )
}

class CampaignHeader extends React.Component<
  Props,
  {
    isOpenMaximumRecipientsModal: boolean
  }
> {
  state = {
    isOpenMaximumRecipientsModal: false,
  }
  _refreshTimer: ReturnType<typeof setTimeout> | null = null

  componentWillUnmount() {
    if (this._refreshTimer) clearTimeout(this._refreshTimer)
  }

  componentDidMount() {
    const countContacts = this.props.filteredContacts.length
    this.setState({
      isOpenMaximumRecipientsModal: countContacts > MAXIMUM_RECIPIENTS,
    })
  }

  handleCloseConfirmationDialog = () => {
    this.setState({
      isOpenMaximumRecipientsModal: false,
    })
  }

  render() {
    const {
      campaign,
      nextButton,
      nextDisabled,
      onNextClick,
      filteredContacts,
      customPageTitle,
    } = this.props
    const { isOpenMaximumRecipientsModal } = this.state
    const countContacts = filteredContacts.length
    let pageTitle = BRIEFING_ENABLED
      ? 'Campaign/Briefing builder'
      : 'Email creator'
    pageTitle = customPageTitle || pageTitle
    let title = null

    if (campaign) {
      const { name, type } = campaign
      title = <span>{name || `Untitled ${type}`}</span>
    }

    if (campaign && !campaign.loading) {
      const savedAt = campaign.savedAt

      if (!campaign.saving && savedAt) {
        if (this._refreshTimer) {
          clearTimeout(this._refreshTimer)
        }

        this._refreshTimer = setTimeout(() => {
          if (this) this.forceUpdate()
        }, ONE_MINUTE)
      }
    }

    return (
      <>
        {countContacts > MAXIMUM_RECIPIENTS &&
          isOpenMaximumRecipientsModal &&
          campaign && (
            <ModalDialog
              title="Campaign above limit"
              primaryAction={{
                caption: 'Close',
                onClick: this.handleCloseConfirmationDialog,
              }}
            >
              Sorry! Email Creator campaigns are limited to a maximum of{' '}
              {MAXIMUM_RECIPIENTS} recipients. If you need to send to more
              recipients, try sending across multiple campaigns.
            </ModalDialog>
          )}

        <PageHeader
          breadcrumbs={
            campaign
              ? {
                  content: pageTitle,
                  link: '/campaigns/list',
                }
              : undefined
          }
          title={campaign ? title : pageTitle}
          actions={
            <ButtonContainer>
              <CampaignSaveStatus campaign={campaign} />
              {nextButton || (
                <Button
                  appearance="primary"
                  caption="Next"
                  disabled={nextDisabled}
                  onClick={onNextClick}
                />
              )}
            </ButtonContainer>
          }
        />
      </>
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CampaignHeader) as any
